import { useCallback, useMemo, useRef, useState } from "react";
import { endOfMonth, format, startOfMonth, eachDayOfInterval, getDay } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export type CalendarLocationLite = {
  id: string;
  name: string;
  city: string | null;
  symbolIndex: number;
};

type DateAvailability = {
  locationIds: string[];
  hasVisio: boolean;
  hasHome: boolean;
  hasCabinet: boolean;
};


const toDateKey = (d: Date) => format(d, "yyyy-MM-dd");

// Cache global pour éviter les appels répétés entre les composants
const globalSymbolMap = new Map<string, number>();
const globalHomeLocationIds = new Set<string>();
let globalDefaultLocation: { id: string; name: string; city: string | null } | null = null;
let globalLocationsLoaded = false;

// Charge les lieux une seule fois et assigne les symboles de manière cohérente
async function loadGlobalLocationSymbols(): Promise<Map<string, number>> {
  if (globalLocationsLoaded && globalSymbolMap.size > 0) {
    return globalSymbolMap;
  }

  const { data: locationsData } = await supabase
    .from("locations")
    .select("id, name, city, is_default, is_home_visit")
    .eq("is_active", true)
    .order("is_default", { ascending: false })
    .order("name", { ascending: true });

  globalSymbolMap.clear();
  globalHomeLocationIds.clear();
  globalDefaultLocation = null;
  ((locationsData as any[]) || [])
    .filter((loc) => loc.is_home_visit !== true)
    .forEach((loc, index) => {
      globalSymbolMap.set(loc.id, index);
      if (!globalDefaultLocation) {
        globalDefaultLocation = { id: loc.id, name: loc.name || "Cabinet", city: loc.city ?? null };
      }
    });
  ((locationsData as any[]) || [])
    .filter((loc) => loc.is_home_visit === true)
    .forEach((loc) => globalHomeLocationIds.add(loc.id));
  globalLocationsLoaded = true;

  return globalSymbolMap;
}


// Export pour réutilisation dans TimeSelection
export function getGlobalSymbolIndex(locationId: string): number {
  return globalSymbolMap.get(locationId) ?? 0;
}

export function useCalendarLocationSymbols(enabled: boolean) {
  const [loading, setLoading] = useState(false);
  const [dateMap, setDateMap] = useState<Map<string, DateAvailability>>(new Map());
  const [locations, setLocations] = useState<CalendarLocationLite[]>([]);

  // Cache des défauts par jour de semaine
  const defaultsCache = useRef<Map<number, any[]> | null>(null);
  // Cache des mois déjà chargés
  const loadedMonths = useRef<Set<string>>(new Set());

  const prefetchMonth = useCallback(
    async (monthDate: Date) => {
      if (!enabled) return;

      const monthKey = format(monthDate, "yyyy-MM");
      
      // Éviter de recharger le même mois
      if (loadedMonths.current.has(monthKey)) {
        return;
      }

      setLoading(true);

      try {
        // Charger les symboles globaux une seule fois
        const symbolMap = await loadGlobalLocationSymbols();

        // Charger les défauts une seule fois (7 jours en parallèle)
        if (!defaultsCache.current) {
          const promises = [0, 1, 2, 3, 4, 5, 6].map((dow) =>
            supabase.rpc("get_default_availability_with_location", { p_day_of_week: dow })
          );
          const results = await Promise.all(promises);
          
          const dayMap = new Map<number, any[]>();
          results.forEach((res, idx) => {
            dayMap.set(idx, (res.data as any[]) || []);
          });
          defaultsCache.current = dayMap;
        }

        const defaults = defaultsCache.current;
        const start = startOfMonth(monthDate);
        const end = endOfMonth(monthDate);
        const days = eachDayOfInterval({ start, end });

        // Charger tous les overrides du mois en parallèle (max ~31 appels)
        const overridePromises = days.map((day) => {
          const dateKey = toDateKey(day);
          return supabase
            .rpc("get_public_availability_with_location", { p_date: dateKey })
            .then((res) => ({ dateKey, day, data: res.data }));
        });

        const overrideResults = await Promise.all(overridePromises);

        const nextDateMap = new Map<string, DateAvailability>();
        const rawLocations = new Map<string, { id: string; name: string; city: string | null }>();

        for (const { dateKey, day, data } of overrideResults) {
          const overrides = (data as any[]) || [];
          const rows = overrides.length > 0 ? overrides : (defaults.get(getDay(day)) || []);

          const locationIds = new Set<string>();
          let hasVisio = false;
          let hasHome = false;
          let hasCabinet = false;
          
          for (const r of rows) {
            if (!r?.is_available) continue;
            
            // Visio is available if:
            // - online_only = true (visio only), OR
            // - in_office_only is NOT true (means both modes available or visio available)
            if (r.online_only === true || r.in_office_only !== true) {
              hasVisio = true;
            }

            // Consultation à domicile explicitement activée
            if (r.home_visit === true) {
              hasHome = true;
            }

            const isHomeLocation = r.location_id ? globalHomeLocationIds.has(r.location_id) : false;

            // Créneau présentiel : pas de visio exclusive et pas un lieu « à domicile »
            if (r.online_only !== true && !isHomeLocation) {
              hasCabinet = true;
            }
            
            // Collect cabinet locations (pour les symboles de la légende)
            if (r.online_only !== true && !isHomeLocation) {
              const locId = r.location_id || globalDefaultLocation?.id;
              if (locId) {
                locationIds.add(locId);
                rawLocations.set(locId, {
                  id: locId,
                  name: (r.location_id ? r.location_name : globalDefaultLocation?.name) || "Cabinet",
                  city: (r.location_id ? r.location_city : globalDefaultLocation?.city) || null,
                });
              }
            }
          }

          if (locationIds.size > 0 || hasVisio || hasHome || hasCabinet) {
            nextDateMap.set(dateKey, { locationIds: Array.from(locationIds), hasVisio, hasHome, hasCabinet });
          }


        }

        // Mettre à jour le dateMap en conservant les données existantes
        setDateMap((prev) => {
          const merged = new Map(prev);
          nextDateMap.forEach((v, k) => merged.set(k, v));
          return merged;
        });

        // Construire la liste des lieux avec symboles cohérents
        // IMPORTANT: merge with existing locations so symbols don't disappear when
        // navigating across months and coming back.
        const sortedLocations = Array.from(rawLocations.values()).sort((a, b) => a.name.localeCompare(b.name));
        const withSymbols: CalendarLocationLite[] = sortedLocations.map((l) => ({
          ...l,
          symbolIndex: symbolMap.get(l.id) ?? 0,
        }));

        setLocations((prev) => {
          const byId = new Map<string, CalendarLocationLite>(prev.map((l) => [l.id, l]));
          for (const l of withSymbols) byId.set(l.id, l);
          return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
        });

        loadedMonths.current.add(monthKey);
      } catch (e) {
        console.error("[useCalendarLocationSymbols] prefetchMonth failed", e);
      } finally {
        setLoading(false);
      }
    },
    [enabled]
  );

  const symbolIndexByLocationId = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of locations) m.set(l.id, l.symbolIndex);
    return m;
  }, [locations]);

  return {
    loading,
    locations,
    dateMap,
    symbolIndexByLocationId,
    prefetchMonth,
  };
}
